import { inject, Injectable } from '@angular/core';
import { TableService } from '../../../../core/components/table/table.service';
import { MatDialog } from '@angular/material/dialog';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import {
  forkJoin,
  from,
  MonoTypeOperatorFunction,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { AlertService } from '../../../../core/services/alert.service';
import { PackingBoxViewService } from '../../services/view.service';
import { PackingBoxSalesOrderRankComponent } from '../../dialog/so-rank/so-rank.component';
import { PackingBoxViewAuthService } from '../../services/auth.service';
import { PackingBoxTableZebraApiService } from '../../api/table-api-zebra.service';
import { PackingBoxLabelCreatorApiService } from '../../api/label-creator.service';
import { ZebraService } from 'src/app/core/services/zebra.service';
import { CacheTableService } from '../../../../common/packing-box/services/cache-table.service';
import { CompoundingPackingBoxViewApiService } from 'src/app/features/compounding/api/view-packing-box.service';
import { PackingBoxViewApiService } from '../../api/view.service';
import {
  PackingBoxCreateLabelType,
  PrintLabelData,
} from '../../types/create-label-dialog';
import { AssemblyItem } from 'src/app/api/elite/types/assembly-item';
import { LotNumber } from 'src/app/api/elite/types/transaction-data';
import { CompoundingPackingBoxCreateLabelComponent } from 'src/app/features/compounding/dialog/create-label/create-label.component';
import {
  CompoundingSelectPartNumberComponent,
  CompoundingSelectPartNumberType,
} from 'src/app/features/compounding/dialog/select-part-number/select-part-number.component';
import { CompoundingPackingSimpleLabelComponent } from 'src/app/features/compounding/dialog/simple-batch/simple-batch.component';
import { PackingBoxTableRow } from '../../../../common/packing-box/models/table-row';
import { NormalLabelComponent } from '../../../../common/packing-box/dialog/normal-label/normal-label.component';
import { PackingBoxSalesOrderItem } from '../../../../common/packing-box/models/sales-orders';
import { ApiRouteLabelService } from 'src/app/api/elite/routes/label.service';
import { ApiRouteInspectionAssemblyItemService } from 'src/app/api/elite/routes/inspection-assembly-item.service';

/** Categorías de error del flujo de impresión de packing box. */
type PackingBoxPrintErrorKind =
  | 'printer-not-found'
  | 'print-failed'
  | 'label-code'
  | 'unknown';

interface PackingBoxPrintError {
  kind: PackingBoxPrintErrorKind;
  cause?: unknown;
}

const PACKING_BOX_PRINT_ERROR_MESSAGES: Record<
  PackingBoxPrintErrorKind,
  string
> = {
  'printer-not-found':
    'No se encontró la impresora. Verifica que esté conectada y configurada.',
  'print-failed': 'No se pudo imprimir la etiqueta correctamente.',
  'label-code': 'Hubo un error al obtener el código de la etiqueta.',
  unknown: 'Ocurrió un error inesperado. Intenta de nuevo.',
};

@Injectable()
export class PackingBoxZebraTableService extends TableService<PackingBoxTableRow> {
  private readonly cacheTableService = inject(CacheTableService);

  protected readonly apiRouter = inject(
    PackingBoxViewApiService
  ) as CompoundingPackingBoxViewApiService;
  private readonly apiRouterPrint = inject(PackingBoxTableZebraApiService);
  private readonly labelCreator = inject(PackingBoxLabelCreatorApiService);
  private readonly alertService = inject(AlertService);
  private readonly dialog = inject(MatDialog);
  private readonly viewService = inject(PackingBoxViewService);
  private readonly zebraService = inject(ZebraService);
  private readonly authService = inject(PackingBoxViewAuthService);
  protected readonly labelService = inject(ApiRouteLabelService);
  private readonly inspectionAssemblyItemService = inject(ApiRouteInspectionAssemblyItemService);
  department = this.authService.Deparment;
  createLabel(row: PackingBoxTableRow): void {
    const transactionNumber = row.transactionNumber;

    const isCalculated =
      this.cacheTableService.getSaleOrderCalculated(transactionNumber);

    if (isCalculated) {
      this.openCreateLabelModal(
        row.transactionNumber,
        row.quantity,
        row.department,
        row.transactionId,
        row
      );
      return;
    }
    this.cacheTableService
      .getDatosFromIndex(row)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.openCreateLabelModal(
            row.transactionNumber,
            row.quantity,
            row.department,
            row.transactionId,
            row
          );
        },
        error: (err) => {
          this.alertService.error('Error obteniendo datos: ' + err);
          console.error('Error obteniendo datos', err);
        },
      });
  }
  setSalesOrderRank(row: PackingBoxTableRow): void {
    const transactionNumber = row.transactionNumber;
    const isCalculated =
      this.cacheTableService.getSaleOrderCalculated(transactionNumber);

    if (isCalculated) {
      this.openSalesOrderRankDialog(transactionNumber);
      return;
    }

    this.cacheTableService
      .getDatosFromIndex(row)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.openSalesOrderRankDialog(transactionNumber);
        },
        error: (err) => {
          this.alertService.error('Error obteniendo datos: ' + err);
          console.error('Error obteniendo datos', err);
        },
      });
  }

  private openSalesOrderRankDialog(transactionNumber: string): void {
    this.dialog
      .open(PackingBoxSalesOrderRankComponent, {
        data: { rowId: transactionNumber },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.cacheTableService
            .updateTransactionRange(transactionNumber, result)
            .subscribe((data) => {
              if (data) {
                this.alertService.success('Se guaradron los datos con exito');
              }
            });
        }
      });
  }
  openCreateLabelModal(
    transactionNumber: string,
    maxQuantity: number,
    department: string,
    transactionId: number,
    row: PackingBoxTableRow
  ): void {
    const salesOrders =
      this.cacheTableService.getSaleOrderCalculated(transactionNumber);
    const lotNumber = this.cacheTableService.getNumeroDeLote(transactionNumber);
    let lotNumberList = [];
    if (lotNumber) {
      lotNumberList = [lotNumber.title];
    }

    let maxQuantitySO = 0;
    let selectSalesOrder = false;
    let salesOrder = 'Stock';
    let salesOrderSelected: PackingBoxSalesOrderItem;

    if (salesOrders.length > 0) {
      salesOrderSelected = salesOrders[0];

      salesOrder = salesOrderSelected.title;
      const fromBack =
        this.cacheTableService.getSalesOrderFromBackend(transactionNumber);

      const order = fromBack?.find((data) => data.title === salesOrder);

      const line = this.cacheTableService.getTransactionLineWithLowestRank(
        order?.lines
      );

      maxQuantitySO = line
        ? Number(line.stats?.quantityRemaining ?? line.quantityAsked)
        : salesOrderSelected.quantityAsked;

      selectSalesOrder = true;
    }
    const modalData: PackingBoxCreateLabelType['data'] = {
      maxQuantitySO,
      maxQuantity,
      isToStock: salesOrder === 'Stock',
      lotNumbers: lotNumberList,
      salesOrder,
      selectSalesOrder,
      department,
    };

    const wo = this.cacheTableService.getWorkoreder(transactionNumber);
    const assembly = wo.assemblyItem;
    if (department === 'Compounding') {
      this.compudingModalAction(
        transactionId,
        maxQuantity,
        assembly,
        lotNumberList,
        row,
        department
      );
    } else {
      this.normalModalAction(
        modalData,
        transactionNumber,
        salesOrderSelected,
        salesOrder,
        row
      );
    }
    if (this.cacheTableService.getErrorSalesOrder(transactionNumber)) {
      this.cacheTableService.removeCacheSalesOrder(transactionNumber);
    }
  }
  compudingModalAction(
    transactionId: number,
    quantity: number,
    assembly: AssemblyItem,
    lotNumber: LotNumber[],
    row: PackingBoxTableRow,
    department: string
  ): void {
    const type = assembly.assemblyTypes.find(
      (data) => data.title === 'Compounding'
    );
    this.labelService
      .sharedPrintersByDepartment(department) // Getting all shared printers by this department
      .subscribe((printers) => {
        if (type) {
          this.apiRouter.simpleBatches(transactionId).subscribe((data) => {
            this.dialog
              .open(CompoundingPackingSimpleLabelComponent, {
                data: {
                  maxQuantity: quantity,
                  isToStock: true,
                  lotNumbers: lotNumber,
                  batches: data,
                  salesOrder: undefined,
                  printers,
                  department,
                },
              })
              .afterClosed()
              .pipe(
                filter((res) => !!res),

                switchMap((res) =>
                  this.apiRouter.itemsIsUsedOn(assembly.title).pipe(
                    switchMap((partNumbers) =>
                      this.dialog
                        .open(CompoundingSelectPartNumberComponent, {
                          data: { partNumbers },
                          minWidth: '80%',
                        })
                        .afterClosed()
                        // todo remover el pipe
                        .pipe(
                          map(
                            (
                              selected: CompoundingSelectPartNumberType['result']
                            ) => ({
                              res,
                              selected,
                            })
                          )
                        )
                    ),

                    map(({ res, selected }) => {
                      return selected?.success
                        ? {
                            ...res,
                            itemName: selected.partNumber
                              ? selected.partNumber
                              : assembly.title,
                            description: assembly.description,
                            lotNumbers: lotNumber,
                            partNumberByCustomer: selected.partNumber
                              ? assembly.title
                              : '',
                            batchId: { id: res.workOrderBatchId },

                            batches: data,
                          }
                        : null;
                    }),
                    filter(
                      (result): result is PrintLabelData => result !== null
                    )
                  )
                )
              )
              .subscribe((finalResult) => {
                this.sendToPrintLabel(finalResult, row);
              });
          });
        } else {
          this.apiRouter
            .relationWithBatches(transactionId)
            .subscribe((data) => {
              this.dialog
                .open(CompoundingPackingBoxCreateLabelComponent, {
                  data: {
                    maxQuantity: quantity,
                    isToStock: true,
                    lotNumbers: lotNumber,
                    batches: data,
                    salesOrder: undefined,
                    printers,
                    department,
                  },
                })
                .afterClosed()
                .pipe(
                  filter((res) => !!res),

                  switchMap((res) =>
                    this.apiRouter.itemsIsUsedOn(assembly.title).pipe(
                      switchMap((partNumbers) =>
                        this.dialog
                          .open(CompoundingSelectPartNumberComponent, {
                            data: { partNumbers },
                            minWidth: '80%',
                          })
                          .afterClosed()
                          // todo remover el pipe
                          .pipe(
                            map(
                              (
                                selected: CompoundingSelectPartNumberType['result']
                              ) => ({
                                res,
                                selected,
                              })
                            )
                          )
                      ),

                      map(({ res, selected }) => {
                        return selected?.success
                          ? {
                              ...res,
                              itemName: selected.partNumber
                                ? selected.partNumber
                                : assembly.title,
                              description: assembly.description,
                              lotNumbers: lotNumber,
                              partNumberByCustomer: selected.partNumber
                                ? assembly.title
                                : '',
                              compounding: {
                                workOrderBatchId: res.workOrderBatchId,
                                assemblyItem: selected.partNumber,
                              },
                              batches: data,
                              salesOrder: 'Stock',
                            }
                          : null;
                      }),

                      filter(
                        (result): result is PrintLabelData => result !== null
                      )
                    )
                  )
                )
                .subscribe((finalResult) => {
                  this.sendToPrintLabel(finalResult, row);
                });
            });
        }
      });
  }

  normalModalAction(
    data: PackingBoxCreateLabelType['data'],
    transactionNumber: string,
    salesOrderSelected: PackingBoxSalesOrderItem,
    salesOrder: string,
    row: PackingBoxTableRow
  ): void {
    const wo = this.cacheTableService.getWorkoreder(transactionNumber);
    const assembly = wo.assemblyItem;

    forkJoin({
      printers: this.labelService.sharedPrintersByDepartment(data.department),
      assemblyInfo: this.inspectionAssemblyItemService.getMeasureByName(assembly.title, transactionNumber).pipe(catchError(() => of(null))),
    }).subscribe(({ printers, assemblyInfo }) => {
      const destinations = assemblyInfo?.assemblyItem?.destinations?.map(d => d.destinationName) ?? [];
        this.dialog
          .open(NormalLabelComponent, {
            data: {
              maxQuantitySO: data.maxQuantitySO,
              maxQuantity: data.maxQuantity,
              isToStock: data.salesOrder === 'Stock',
              lotNumbers: data.lotNumbers,
              salesOrder: data.salesOrder,
              selectSalesOrder: data.selectSalesOrder,
              department: data.department,
              printers,
              destinations,
            },
          })
          .afterClosed()
          .subscribe((res) => {
            if (res) {
              if (res.addSalesOrder) {
                this.createSalesOrder(
                  transactionNumber,
                  salesOrderSelected,
                  salesOrder
                );
              }
              console.log(salesOrder, salesOrderSelected);

              const printLabel: PrintLabelData = {
                ...res,
                salesOrder: res.addSalesOrder ? data.salesOrder : null,
                salesOrderLineId: res.addSalesOrder
                  ? salesOrderSelected?.id
                  : null,
                description: assembly.description,
                partNumberByCustomer: salesOrderSelected?.partNumberByCustomer,
                purchaseOrder: salesOrderSelected?.purchaseOrderTitle,
              };

              this.sendToPrintLabel(
                printLabel,
                row,
                transactionNumber,
                res.printerName,
                res.printerDepartment
              );
            }
          });
      });
  }
  validateAddSaleOrder(transactionNumber: string, salesOrder): boolean {
    const list =
      this.cacheTableService.getSalesOrderFromBackend(transactionNumber);
    if (!list) {
      return false;
    }
    return !!list.find((data) => data.title === salesOrder);
  }
  createSalesOrder(
    transactionNumber: string,
    salesOrderSelected: PackingBoxSalesOrderItem,
    salesOrder: string
  ): void {
    if (salesOrderSelected) {
      const validate = this.validateAddSaleOrder(transactionNumber, salesOrder);
      if (!validate) {
        const wo = this.cacheTableService.getWorkoreder(transactionNumber);
        const netsuiteSo =
          this.cacheTableService.getSaledOrderFromNestsuite(transactionNumber);
        const serach = netsuiteSo.find((data) => data.saleOrder === salesOrder);
        console.log('salesOrder', salesOrderSelected);
        this.cacheTableService.makeSalesOrder(
          salesOrderSelected,
          serach,
          wo.assemblyItem,
          transactionNumber
        );
      }
    }
  }
  private sendToPrintLabel(
    res: PrintLabelData,
    row: PackingBoxTableRow,
    transactionNumber?: string,
    printerName?: string,
    departmentName?: string
  ): void {
    let createdIds: number[] = [];
    this.apiRouterPrint
      .createPackingBox({
        workOrderId: row.transactionId,
        quantity: res.quantity,
        numLabels: res.numLabels,
        salesOrderLineId: res.salesOrderLineId,
        compounding: res.compounding,
        batch: res.batchId,
      })
      .pipe(
        tap((ids) => (createdIds = ids)),

        switchMap((ids) =>
          this.viewService.brandType$.pipe(
            take(1),
            map((brandType) => ({ brandType, ids }))
          )
        ),

        switchMap(({ brandType, ids }) => {
          const barcode = this.department === 'Polyurethane';
          let clientName = res.partNumberByCustomer;
          if (
            !res.batch &&
            (res.salesOrder === 'Stock' || res.salesOrder === null)
          ) {
            clientName = null;
          }
          // Pre-flight: ask the elite backend whether this work order /
          // sales order combination needs a customer-specific private
          // label. If it does, forward the response straight into
          // `createLabel` (it already matches a customer branch of
          // `CreateLabelArgs`). If `labelType` comes back `null`, build
          // the standard packing-box body from local context and call
          // `createLabel` with that instead.
          return this.labelService
            .informationForPrivateLabel(row.transactionId, res.salesOrder ?? '')
            .pipe(
              switchMap((info) => {
                if (info.labelType !== null) {
                  return this.labelCreator.createLabel({
                    ...info,
                    quantity: res.quantity,
                  });
                }
                return this.labelCreator.createLabel({
                  data: {
                    type:
                      res.salesOrder === 'Stock'
                        ? 'packing-box-stock'
                        : 'packing-box',
                    part_number_client: clientName,
                    part_number_elite: res.itemName
                      ? res.itemName
                      : row.itemName,
                    description: res.description,
                    quantity: res.quantity,
                    sales_order: res.salesOrder,
                    work_order: row.transactionNumber,
                    po: res?.purchaseOrder || '',
                    lot: res.lotNumberSelected || '',
                    legacy_ids: [],
                    batch: res?.batch || '',
                    ids,
                    brand: brandType,
                    woBarcode: barcode,
                  },
                });
              }),
              // Errores al obtener el código (ZPL) de la etiqueta.
              this.tagError('label-code'),
              switchMap((response) =>
                forkJoin(
                  response.map((zpl) =>
                    this.printSingleLabel(
                      zpl,
                      res,
                      row,
                      printerName,
                      departmentName
                    )
                  )
                ).pipe(map(() => response))
              )
            );
        }),

        catchError((err) => {
          console.log('enters error', createdIds);
          if (createdIds.length > 0) {
            return this.apiRouterPrint
              .deletePackingBox(createdIds)
              .pipe(switchMap(() => throwError(() => err)));
          }
          return throwError(() => err);
        })
      )
      .subscribe(
        (data) => {
          if (data === undefined || data === null) {
            this.alertService.error('No se pudieron guardar los datos');
            return;
          }

          row.quantity -= res.quantity * createdIds.length;
          if (res.salesOrder) {
            const sale =
              this.cacheTableService.getSalesOrderFromBackend(
                transactionNumber
              );

            const saleOrder = sale.find(
              (data) => data.title === res.salesOrder
            );

            if (!saleOrder || saleOrder.lines.length === 0) {
              return;
            }

            const line = saleOrder.lines[0];

            line.quantityFinished += res.quantity * createdIds.length;

            if (line.quantityFinished >= line.quantityAsked) {
              saleOrder.lines.splice(0, 1);
            }

            this.cacheTableService.recalcularSalesOrder(transactionNumber);
          }

          this.list = [
            ...this._list.value.filter(
              (el) => el.transactionId !== row.transactionId
            ),
            row,
          ];
        },
        (err) => {
          const kind: PackingBoxPrintErrorKind =
            err && typeof err === 'object' && 'kind' in err
              ? (err as PackingBoxPrintError).kind
              : 'unknown';
          this.alertService.error(PACKING_BOX_PRINT_ERROR_MESSAGES[kind]);
        }
      );
  }

  /**
   * Imprime una etiqueta (ZPL). Si hay impresora de red la usa; si no, imprime
   * localmente con Zebra. Etiqueta los errores para diferenciar "no se encontró
   * la impresora" (al inicializar Zebra) de "no se pudo imprimir".
   */
  private printSingleLabel(
    zpl: string,
    res: PrintLabelData,
    row: PackingBoxTableRow,
    printerName?: string,
    departmentName?: string
  ): Observable<unknown> {
    if (printerName && departmentName) {
      return this.labelService
        .printLabel({
          namePrinter: printerName,
          department: departmentName,
          quantity: res.quantity,
          quantityOfLabels: res.numLabels,
          workOrderId: row.transactionId,
        })
        .pipe(this.tagError('print-failed'));
    }
    return from(this.zebraService.initPrinter()).pipe(
      this.tagError('printer-not-found'),
      switchMap(() =>
        from(this.zebraService.printZpl(zpl, 1)).pipe(
          this.tagError('print-failed')
        )
      )
    );
  }

  /**
   * Operador que clasifica el error con una categoría (`kind`) para luego
   * mostrar el mensaje correcto. No re-clasifica si el error ya viene
   * etiquetado.
   */
  private tagError<T>(
    kind: PackingBoxPrintErrorKind
  ): MonoTypeOperatorFunction<T> {
    return catchError<T, Observable<never>>((cause: unknown) => {
      if (cause && typeof cause === 'object' && 'kind' in cause) {
        return throwError(() => cause);
      }
      return throwError(() => ({ kind, cause } as PackingBoxPrintError));
    });
  }
}
